import { type IssueData, z } from "zod";

import { postalCodeSchema } from "@/components/Input/PostalCode/postalCode.schema";
import { SupportedCountry } from "@/constants/country";

const CountryCodeRequiredError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.country_code_required" },
};

const StateProvinceRequiredError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.state_province_code_required" },
};

const CityRequiredError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.city_required" },
};

const StreetAddressRequiredError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.street_address_required" },
};

const PostalCodeRequiredError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.postal_code_required" },
};

const PostalCodeInvalidError: IssueData = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.postal_code_invalid" },
};

export interface AddressValidationConfig {
  isCountryRequired?: boolean;
  isStateProvinceRequired?: boolean;
  isCityRequired?: boolean;
  isStreetAddressRequired?: boolean;
  isPostalCodeRequired?: boolean;
}

export const createAddressSchema = ({
  isCountryRequired = true,
  isStateProvinceRequired = true,
  isCityRequired = true,
  isStreetAddressRequired = true,
  isPostalCodeRequired = true,
}: AddressValidationConfig = {}) => {
  const baseSchema = z.object({
    countryCode: z.nativeEnum(SupportedCountry).superRefine((val, ctx) => {
      if (isCountryRequired && !val) {
        ctx.addIssue(CountryCodeRequiredError);
      }
    }),
    stateProvinceCode: z.string().superRefine((val, ctx) => {
      if (isStateProvinceRequired && !val) {
        ctx.addIssue(StateProvinceRequiredError);
      }
    }),
    city: z.string().superRefine((val, ctx) => {
      if (isCityRequired && !val) {
        ctx.addIssue(CityRequiredError);
      }
    }),
    streetAddress: z.string().superRefine((val, ctx) => {
      if (isStreetAddressRequired && !val) {
        ctx.addIssue(StreetAddressRequiredError);
      }
    }),
    addressLine2: z.string().optional(),
    postalCode: z.string(),
  });

  return baseSchema.superRefine((data, ctx) => {
    if (isPostalCodeRequired && !data.postalCode) {
      ctx.addIssue({
        ...PostalCodeRequiredError,
        path: ["postalCode"],
      });
      return;
    }
    if (!data.postalCode) return;

    try {
      postalCodeSchema(data.countryCode).parse(data.postalCode);
    } catch {
      ctx.addIssue({
        ...PostalCodeInvalidError,
        path: ["postalCode"],
      });
    }
  });
};

export type AddressFormValues = z.infer<ReturnType<typeof createAddressSchema>>;
